import { Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { TrialService } from '../trial.service';
import { ApiService } from '../api.service';
import { CreateProjectRequest, ProjectCreatedResponse } from './project.types';

export interface ClientProjectUpdateRequestDto {
  clientId: string;
  projectId: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  constructor(
    private readonly apiService: ApiService,
    private readonly trialService: TrialService
  ) { }

  getProjectsList(query: string, page: number = 0, size: number = 10) {
    const params = new URLSearchParams();
    if (query) {params.set('query', query);}
    params.set('page', page.toString());
    params.set('size', size.toString());

    return this.apiService.apiGet(`projects?${params.toString()}`).pipe(
      switchMap(response => {
        const projects = response?.data ?? [];

        if (!projects.length) {
          return of(response);
        }

        const enrichedProjects$ = projects.map(project =>
          this.enrichProjectWithClients(project)
        );

        return forkJoin(enrichedProjects$).pipe(
          map(enrichedProjects => ({
            ...response,
            data: enrichedProjects
          }))
        );
      })
    );
  }

  private enrichProjectWithClients(project: any) {
    return this.getClientByProjectId(project?.id).pipe(
      map(clients => {
        const clientNames = this.extractClientNames(clients);
        return {
          ...project,
          clientNames
        };
      }),
      catchError(() => of({
        ...project,
        clientNames: []
      }))
    );
  }

  private extractClientNames(clients: any[]): string[] {
    return clients
      .map(c => c?.client?.name)
      .filter(name => name !== null);
  }

  getClientByProjectId(projectId: string) {
    const params = new URLSearchParams();
    if (projectId) {params.set('projectId', projectId);}

    return this.apiService.apiGet(`client-projects?${params.toString()}`).pipe(
      map(response => response?.data ?? []),
      catchError(() => of([]))
    );
  }

  createProject(projectData: CreateProjectRequest): Observable<ProjectCreatedResponse> {
    return this.apiService.apiPost('projects', projectData);
  }

  createClientProject(projectData: ClientProjectUpdateRequestDto) {
    return this.apiService.apiPost('client-projects', projectData);
  }

  oldgetProjectDetails(projectId: string) {
    return this.apiService.apiGet(`projects/${projectId}`);
  }

  getProjectDetails(projectId: string) {
    return forkJoin({
      details: this.apiService.apiGet(`projects/${projectId}`),
      clients: this.getClientByProjectId(projectId),
      trialList: this.trialService.getTrialsList('',projectId)
    }).pipe(
      map(({ details, clients, trialList }) => {
        const firstClient = clients?.[0]?.client ?? null;
        const clientNames = this.extractClientNames(clients);
        return {
          details,
          clientId: firstClient?.id ?? null,
          clientName: clientNames.length ? clientNames.join(', ') : null,
          trialList
        };
      })
    );
  }

  updateProject(projectData: any, projectId: string) {
    return this.apiService.apiPut('projects', projectData, projectId);
  }

  getDeviceCountByProjectId(projectId: string) {
    return this.apiService.apiGet(`projects/${projectId}/summary`);
  }

}
